import { Card, ListGroup } from 'react-bootstrap';
import { Dispatch, FC, SetStateAction } from 'react';
import { generalHthHelper } from '@monorepo/helpers';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { hthConfigOptions } from 'src/app-config';
import { logServices } from 'src/services';
import { typeLogObj, typeLogsDoc } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetConfig, useGetCurrentUser, useGetIsDarkMode } from 'src/custom-hooks';

type propsType = {
    activeLog: typeLogsDoc;
    dateRange: {
        maxDate: string | null;
        minDate: string | null;
    } | null;
    setLogs: Dispatch<SetStateAction<typeLogsDoc[] | null>>;
};

export const LogsCard: FC<propsType> = ({ activeLog, dateRange, setLogs }) => {
    const config = useGetConfig();
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();
    const user = useGetCurrentUser();

    const maskBlockAndFaceHandler = (log: string) => {
        hthConfigOptions.blocks.forEach(b => {
            log = log.replace(`manzana ${b}`, `manzana ${generalHthHelper.MaskTheBlock(b, config.isUsingLettersForBlocks)}`);
            log = log.replace(`Manzana ${b}`, `Manzana ${generalHthHelper.MaskTheBlock(b, config.isUsingLettersForBlocks)}`);
        });
        hthConfigOptions.faces.forEach(f => {
            log = log.replace(`cara ${f}`, `cara ${generalHthHelper.MaskTheFace(f, config.isUsingLettersForBlocks)}`);
            log = log.replace(`Cara ${f}`, `Cara ${generalHthHelper.MaskTheFace(f, config.isUsingLettersForBlocks)}`);
        });
        return log;
    }

    const getLogPage = async (offset: number): Promise<typeLogsDoc | null> => {
        const response = await logServices.GetAll({
            congregation: activeLog.congregation,
            maxDate: dateRange?.maxDate,
            minDate: dateRange?.minDate,
            offset,
            type: activeLog.type
        });
        const log = response.data?.log;
        if (!response.success || !log) {
            dispatch(setAlertResponse({ response }));
            return null;
        }
        return log;
    }

    const setUpdatedLog = (updatedLog: typeLogsDoc): void => {
        setLogs(currentLogs => currentLogs?.map(log =>
            log.id === updatedLog.id ? updatedLog : log
        ) ?? null);
    }

    const loadMore = async (): Promise<void> => {
        dispatch(showLoadingModalReducer());
        const page = await getLogPage(activeLog.logs.length);
        dispatch(hideLoadingModalReducer());
        if (!page) {
            return;
        }
        setUpdatedLog({
            ...activeLog,
            logs: [...activeLog.logs, ...page.logs],
            hasMore: page.logs.length === 100
        });
    }

    const reloadDisplayedLogs = async (): Promise<void> => {
        const updatedLogs: typeLogObj[] = [];
        let hasMore = false;
        for (let offset = 0; offset < activeLog.logs.length; offset += 100) {
            const page = await getLogPage(offset);
            if (!page) {
                return;
            }
            updatedLogs.push(...page.logs);
            hasMore = page.logs.length === 100;
            if (page.logs.length < 100) {
                break;
            }
        }
        setUpdatedLog({
            ...activeLog,
            logs: updatedLogs,
            hasMore
        });
    }

    const deleteLog = (log: typeLogObj) => {
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: '¿Eliminar?',
            animation: 3,
            message: `Se va a eliminar este log de '${activeLog.title}' de la congregación ${activeLog.congregation}: ${log.logText}`,
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await logServices.Delete(activeLog.congregation, log);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                await reloadDisplayedLogs();
            }
        }));
    }

    return (
        <Card className={`${isDarkMode ? 'bg-dark text-white' : ''} my-4`}>
            <Card.Header className={'text-center h1 py-4'}>
                <div className={'mb-3'}>
                    {activeLog.title} <span className={'small fs-5'}>(viendo {activeLog.logs.length})</span>
                </div>
            </Card.Header>
            <ListGroup variant={'flush'} className={'border-top border-bottom'}>
                {activeLog.logs.map((log, i) =>
                    <ListGroup.Item key={i}
                        className={`d-flex justify-content-between align-items-center ${isDarkMode ? 'bg-dark text-white' : ''}`}
                    >
                        <span>
                            {maskBlockAndFaceHandler(log.logText)}
                        </span>
                        {user.isSuperAdmin &&
                            <button className={'btn btn-general-red btn-sm'}
                                onClick={() => deleteLog(log)}
                            >
                                Eliminar
                            </button>
                        }
                    </ListGroup.Item>
                )}
            </ListGroup>
            <Card.Header className={'text-center h1 py-4'}>
                {!!activeLog.logs.length && activeLog.logs.length % 100 === 0 && activeLog.hasMore !== false &&
                    <button className={'btn btn-general-blue d-block mt-3 mx-auto'} onClick={() => void loadMore()}>
                        Traer 100 más
                    </button>
                }
            </Card.Header>
        </Card>
    );
}
